import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    Typography,
    Box,
    Paper,
    Divider,
    Chip,
    Tooltip,
    IconButton,
    Fade,
    Button,
    CircularProgress,
    TextField,
    MenuItem,
    Snackbar,
    Stack,
    ToggleButton,
    ToggleButtonGroup,
    useTheme,
    useMediaQuery
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import PublicIcon from '@mui/icons-material/Public';
import TokenIcon from '@mui/icons-material/Token';
import { useTranslation } from 'react-i18next';
import { useWebSocket } from './WebSocketContext';
import { fetchProjectAiTokenUsage } from '../Utils/actionsApi';
import { fetchSavedModels } from '../Utils/httpCommandBridge';
import { fetchProject } from '../Utils/projectDataApi';
import {
    Bar,
    CartesianGrid,
    ComposedChart,
    Legend,
    Line,
    ResponsiveContainer,
    Tooltip as RechartsTooltip,
    XAxis,
    YAxis
} from 'recharts';

const SettingAI = ({ caseid }) => {
    const { t } = useTranslation();
    const { sendMessage, subscribeToMessages } = useWebSocket();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [isVisible, setIsVisible] = useState(false);

    const [projectModelProvider, setProjectModelProvider] = useState('');
    const [projectTextModelId, setProjectTextModelId] = useState('');
    const [projectTextModelProvider, setProjectTextModelProvider] = useState('');
    const [projectVisionModelId, setProjectVisionModelId] = useState('');
    const [projectVisionModelProvider, setProjectVisionModelProvider] = useState('');
    const [projectAudioModelId, setProjectAudioModelId] = useState('');
    const [projectAudioModelProvider, setProjectAudioModelProvider] = useState('');

    // Track original values so we can determine whether user changed anything
    const [initialProjectModel, setInitialProjectModel] = useState({
        provider: '',
        textProvider: '',
        textId: '',
        visionProvider: '',
        visionId: '',
        audioProvider: '',
        audioId: ''
    });
    const [projectModelDirty, setProjectModelDirty] = useState(false);
    const [savedModels, setSavedModels] = useState({ text: [], vision: [], audio: [] });
    const [projectModelSaving, setProjectModelSaving] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [tokenUsagePeriod, setTokenUsagePeriod] = useState('day');
    const [tokenUsageLoading, setTokenUsageLoading] = useState(false);
    const [tokenUsage, setTokenUsage] = useState({
        period: 'day',
        series: [],
        providers: [],
        totals: {
            input_tokens: 0,
            output_tokens: 0,
            total_tokens: 0,
            request_count: 0
        }
    });

    // Trigger animation when component mounts
    useEffect(() => {
        const timer = setTimeout(() => {
            setIsVisible(true);
        }, 100);
        return () => clearTimeout(timer);
    }, []);

    const loadSettings = useCallback(() => {
        fetchSavedModels()
            .then((payload) => {
                setSavedModels(payload?.saved_models || payload || { text: [], vision: [], audio: [] });
            })
            .catch((error) => {
                console.error('Error loading saved models:', error);
            });
    }, []);

    useEffect(() => {
        if (caseid) {
            loadSettings();
        }
    }, [caseid, loadSettings]);

    useEffect(() => {
        if (!caseid) {
            return undefined;
        }

        let cancelled = false;
        fetchProject(caseid)
            .then((payload) => {
                if (cancelled) {
                    return;
                }
                const projectCase = payload?.case;
                if (!projectCase) {
                    return;
                }
                setProjectModelProvider(projectCase.project_model_provider || '');
                setProjectTextModelProvider(projectCase.project_text_model_provider || '');
                setProjectTextModelId(projectCase.project_text_model_id ? String(projectCase.project_text_model_id) : '');
                setProjectVisionModelProvider(projectCase.project_vision_model_provider || '');
                setProjectVisionModelId(projectCase.project_vision_model_id ? String(projectCase.project_vision_model_id) : '');
                setProjectAudioModelProvider(projectCase.project_audio_model_provider || '');
                setProjectAudioModelId(projectCase.project_audio_model_id ? String(projectCase.project_audio_model_id) : '');
                setInitialProjectModel({
                    provider: projectCase.project_model_provider || '',
                    textProvider: projectCase.project_text_model_provider || '',
                    textId: projectCase.project_text_model_id ? String(projectCase.project_text_model_id) : '',
                    visionProvider: projectCase.project_vision_model_provider || '',
                    visionId: projectCase.project_vision_model_id ? String(projectCase.project_vision_model_id) : '',
                    audioProvider: projectCase.project_audio_model_provider || '',
                    audioId: projectCase.project_audio_model_id ? String(projectCase.project_audio_model_id) : ''
                });
                setProjectModelDirty(false);
            })
            .catch((error) => {
                if (!cancelled) {
                    console.error('Error loading project settings:', error);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [caseid]);

    useEffect(() => {
        if (!caseid) {
            return undefined;
        }
        let cancelled = false;
        setTokenUsageLoading(true);
        fetchProjectAiTokenUsage(caseid, { groupBy: tokenUsagePeriod })
            .then((payload) => {
                if (cancelled) {
                    return;
                }
                if (payload?.status === 'success') {
                    setTokenUsage({
                        period: payload.period || tokenUsagePeriod,
                        series: Array.isArray(payload.series) ? payload.series : [],
                        providers: Array.isArray(payload.providers) ? payload.providers : [],
                        totals: payload.totals || {
                            input_tokens: 0,
                            output_tokens: 0,
                            total_tokens: 0,
                            request_count: 0
                        }
                    });
                } else if (payload?.status === 'error') {
                    setSnackbar({
                        open: true,
                        message: payload.message || t('settings.ai.token_usage_error', 'Failed to load token usage'),
                        severity: 'error'
                    });
                }
            })
            .catch((error) => {
                if (!cancelled) {
                    console.error('Error loading project AI token usage:', error);
                    setSnackbar({
                        open: true,
                        message: error?.message || t('settings.ai.token_usage_error', 'Failed to load token usage'),
                        severity: 'error'
                    });
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setTokenUsageLoading(false);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [caseid, tokenUsagePeriod, t]);

    // Handle the project mutation response from the shared transport bridge.
    useEffect(() => {
        if (!subscribeToMessages) return undefined;

        const handleMessage = (data) => {
            try {
                if (data.command === 'update_project_model_provider') {
                    const payload = data.data || data;
                    setProjectModelSaving(false);
                    if (payload && payload.status === 'success') {
                        setProjectModelProvider(payload.project_model_provider || '');
                        setProjectTextModelProvider(payload.project_text_model_provider || '');
                        // Convert model IDs to strings to match dropdown values
                        setProjectTextModelId(payload.project_text_model_id ? String(payload.project_text_model_id) : '');
                        setProjectVisionModelProvider(payload.project_vision_model_provider || '');
                        setProjectVisionModelId(payload.project_vision_model_id ? String(payload.project_vision_model_id) : '');
                        setProjectAudioModelProvider(payload.project_audio_model_provider || '');
                        setProjectAudioModelId(payload.project_audio_model_id ? String(payload.project_audio_model_id) : '');
                        // After successful save, update the initial values and reset dirty flag
                        setInitialProjectModel({
                            provider: payload.project_model_provider || '',
                            textProvider: payload.project_text_model_provider || '',
                            textId: payload.project_text_model_id ? String(payload.project_text_model_id) : '',
                            visionProvider: payload.project_vision_model_provider || '',
                            visionId: payload.project_vision_model_id ? String(payload.project_vision_model_id) : '',
                            audioProvider: payload.project_audio_model_provider || '',
                            audioId: payload.project_audio_model_id ? String(payload.project_audio_model_id) : ''
                        });
                        setProjectModelDirty(false);
                        setSnackbar({
                            open: true,
                            message: t('settings.ai.project_model_update_success', 'Project AI settings updated'),
                            severity: 'success'
                        });
                    } else {
                        const errorMessage = payload?.message || t('settings.ai.project_model_update_error', 'Failed to update project AI settings');
                        setSnackbar({
                            open: true,
                            message: errorMessage,
                            severity: 'error'
                        });
                    }
                }
            } catch (error) {
                console.error('Error handling project model response:', error);
            }
        };

        return subscribeToMessages(['update_project_model_provider'], handleMessage);
    }, [subscribeToMessages, t]);

    const handleProjectModelSave = () => {
        const valueToSave = (projectTextModelProvider || projectModelProvider || '').trim();
        setProjectModelSaving(true);

        if (sendMessage) {
            try {
                sendMessage({
                    command: 'update_project_model_provider',
                    caseid: caseid,
                    project_model_provider: valueToSave,
                    project_text_model_provider: projectTextModelProvider || '',
                    project_text_model_id: projectTextModelId || '',
                    project_vision_model_provider: projectVisionModelProvider || '',
                    project_vision_model_id: projectVisionModelId || '',
                    project_audio_model_provider: projectAudioModelProvider || '',
                    project_audio_model_id: projectAudioModelId || '',
                });
            } catch (error) {
                console.error('Error sending update_project_model_provider:', error);
                setProjectModelSaving(false);
                setSnackbar({ open: true, message: t('settings.ai.project_model_update_error'), severity: 'error' });
            }
        } else {
            setProjectModelSaving(false);
            setSnackbar({ open: true, message: t('settings.others.update_error', 'Request unavailable'), severity: 'error' });
        }
    };

    const handleModelSelect = (type) => (event) => {
        const value = event.target.value;
        const modelList = savedModels?.[type] || [];
        const selectedModel = modelList.find((model) => String(model.id) === String(value));
        const provider = value ? (selectedModel?.provider || '') : '';

        if (type === 'text') {
            setProjectTextModelId(value);
            setProjectTextModelProvider(provider);
        } else if (type === 'vision') {
            setProjectVisionModelId(value);
            setProjectVisionModelProvider(provider);
        } else if (type === 'audio') {
            setProjectAudioModelId(value);
            setProjectAudioModelProvider(provider);
        }
    };

    const handleTokenUsagePeriodChange = (event, value) => {
        if (value) {
            setTokenUsagePeriod(value);
        }
    };

    const formatTokenNumber = useCallback((value) => {
        const numericValue = Number(value || 0);
        return new Intl.NumberFormat().format(numericValue);
    }, []);

    const tokenUsageChartData = useMemo(() => {
        return (tokenUsage.series || []).map((row) => ({
            period: row.period,
            input_tokens: Number(row.input_tokens || 0),
            output_tokens: Number(row.output_tokens || 0),
            total_tokens: Number(row.total_tokens || 0),
            request_count: Number(row.request_count || 0)
        }));
    }, [tokenUsage.series]);

    const tokenUsageTotals = useMemo(() => ({
        input_tokens: Number(tokenUsage.totals?.input_tokens || 0),
        output_tokens: Number(tokenUsage.totals?.output_tokens || 0),
        total_tokens: Number(tokenUsage.totals?.total_tokens || 0),
        request_count: Number(tokenUsage.totals?.request_count || 0)
    }), [tokenUsage.totals]);

    const topTokenProviders = useMemo(() => {
        return (tokenUsage.providers || []).slice(0, 4).map((provider) => ({
            provider: provider.provider || t('settings.ai.token_usage_unknown_provider', 'Unknown'),
            total_tokens: Number(provider.total_tokens || 0)
        }));
    }, [tokenUsage.providers, t]);

    // Recompute dirty flag whenever any of the model selection values change
    useEffect(() => {
        const dirty = (
            projectModelProvider !== initialProjectModel.provider ||
            projectTextModelProvider !== initialProjectModel.textProvider ||
            (projectTextModelId ? String(projectTextModelId) : '') !== (initialProjectModel.textId ? String(initialProjectModel.textId) : '') ||
            projectVisionModelProvider !== initialProjectModel.visionProvider ||
            (projectVisionModelId ? String(projectVisionModelId) : '') !== (initialProjectModel.visionId ? String(initialProjectModel.visionId) : '') ||
            projectAudioModelProvider !== initialProjectModel.audioProvider ||
            (projectAudioModelId ? String(projectAudioModelId) : '') !== (initialProjectModel.audioId ? String(initialProjectModel.audioId) : '')
        );
        setProjectModelDirty(dirty);
    }, [projectModelProvider, projectTextModelProvider, projectTextModelId, projectVisionModelProvider, projectVisionModelId, projectAudioModelProvider, projectAudioModelId, initialProjectModel]);

    const handleSnackbarClose = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    return (
        <>
            <Fade in={true} timeout={600}>
                <Box sx={{ mb: { xs: 1, sm: 2 } }}>
                    <Typography variant={isMobile ? "h6" : "h5"} gutterBottom sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                        {t("settings.ai.title", "AI Settings")}
                        <Tooltip title={t("settings.ai.description", "Configure AI models for this project")}>
                            <IconButton size="small">
                                <InfoIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Typography>
                </Box>
            </Fade>

            <Fade in={isVisible} timeout={800}>
                <Box
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    gap={{ xs: 2, sm: 3 }}
                    sx={{
                        width: '100%',
                        maxWidth: '800px',
                        mx: 'auto',
                        px: { xs: 0, sm: 2 },
                    }}
                >
                    {/* Project AI model override */}
                    <Box
                        sx={{
                            width: '100%',
                            maxWidth: '600px',
                            p: { xs: 2.5, sm: 4 },
                            borderRadius: { xs: 2, sm: 3 },
                            background: 'linear-gradient(135deg, #ffffff 0%, #f8f9ff 100%)',
                            border: '1px solid rgba(87, 87, 176, 0.08)',
                            boxShadow: '0 4px 20px rgba(87, 87, 176, 0.08)',
                            transition: 'all 0.3s ease',
                            '&:hover': {
                                boxShadow: '0 8px 30px rgba(87, 87, 176, 0.12)',
                                transform: 'translateY(-2px)',
                                borderColor: 'rgba(87, 87, 176, 0.12)',
                            },
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1.5, sm: 2 }, mb: { xs: 2, sm: 3 } }}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: { xs: 40, sm: 48 },
                                    height: { xs: 40, sm: 48 },
                                    borderRadius: 2,
                                    background: 'linear-gradient(135deg, #5757b0 0%, #7777d0 100%)',
                                    boxShadow: '0 4px 15px rgba(87, 87, 176, 0.3)',
                                    flexShrink: 0
                                }}
                            >
                                <PublicIcon sx={{ color: 'white', fontSize: { xs: 20, sm: 24 } }} />
                            </Box>
                            <Box sx={{ flex: 1 }}>
                                <Typography variant={isMobile ? "subtitle1" : "h6"} sx={{ fontWeight: 700, color: '#2d3748', mb: 0.5, lineHeight: 1.2 }}>
                                    {t('settings.ai.project_model_title', 'Project AI model override')}
                                </Typography>
                                <Typography variant="caption" sx={{ color: '#64748b', lineHeight: 1.4, display: 'block' }}>
                                    {t('settings.ai.project_model_helper', 'Choose a specific AI model host for this project or fall back to the global default')}
                                </Typography>
                            </Box>
                        </Box>

                        <TextField
                            select
                            fullWidth
                            size={isMobile ? "small" : "medium"}
                            label={t('settings.ai.project_text_model_label', 'Preferred text model')}
                            value={projectTextModelId}
                            onChange={handleModelSelect('text')}
                            disabled={projectModelSaving}
                            helperText={t('settings.ai.project_model_hint', 'Applies to AI tasks such as title suggestions, descriptions, and widgets')}
                            sx={{ mb: { xs: 1.5, sm: 2 } }}
                            SelectProps={{
                                renderValue: (val) => {
                                    if (!val) return t('settings.ai.project_model_reset', 'Use Global Default');
                                    const model = (savedModels?.text || []).find((m) => String(m.id) === String(val));
                                    return model?.model_name || model?.provider || String(val);
                                }
                            }}
                        >
                            <MenuItem value="">{t('settings.ai.project_model_reset', 'Use global default')}</MenuItem>
                            {(savedModels?.text || []).map((model) => (
                                <MenuItem key={`text-${model.id}`} value={String(model.id)}>
                                    {model.model_name || model.provider}
                                </MenuItem>
                            ))}
                        </TextField>

                        <TextField
                            select
                            fullWidth
                            size={isMobile ? "small" : "medium"}
                            label={t('settings.ai.project_vision_model_label', 'Vision model')}
                            value={projectVisionModelId}
                            onChange={handleModelSelect('vision')}
                            disabled={projectModelSaving}
                            helperText={t('settings.ai.project_vision_model_hint', 'Used for image inputs in this project')}
                            sx={{ mb: { xs: 1.5, sm: 2 } }}
                            SelectProps={{
                                renderValue: (val) => {
                                    if (!val) return t('settings.ai.project_model_reset', 'Use Global Default');
                                    const model = (savedModels?.vision || []).find((m) => String(m.id) === String(val));
                                    return model?.model_name || model?.provider || String(val);
                                }
                            }}
                        >
                            <MenuItem value="">{t('settings.ai.project_model_reset', 'Use global default')}</MenuItem>
                            {(savedModels?.vision || []).map((model) => (
                                <MenuItem key={`vision-${model.id}`} value={String(model.id)}>
                                    {model.model_name || model.provider}
                                </MenuItem>
                            ))}
                        </TextField>

                        <TextField
                            select
                            fullWidth
                            size={isMobile ? "small" : "medium"}
                            label={t('settings.ai.project_audio_model_label', 'Audio model')}
                            value={projectAudioModelId}
                            onChange={handleModelSelect('audio')}
                            disabled={projectModelSaving}
                            helperText={t('settings.ai.project_audio_model_hint', 'Used for speech or audio analysis in this project')}
                            SelectProps={{
                                renderValue: (val) => {
                                    if (!val) return t('settings.ai.project_model_reset', 'Use Global Default');
                                    const model = (savedModels?.audio || []).find((m) => String(m.id) === String(val));
                                    return model?.model_name || model?.provider || String(val);
                                }
                            }}
                        >
                            <MenuItem value="">{t('settings.ai.project_model_reset', 'Use global default')}</MenuItem>
                            {(savedModels?.audio || []).map((model) => (
                                <MenuItem key={`audio-${model.id}`} value={String(model.id)}>
                                    {model.model_name || model.provider}
                                </MenuItem>
                            ))}
                        </TextField>

                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5, mt: { xs: 2.5, sm: 3 } }}>
                            <Button
                                variant="contained"
                                fullWidth={isMobile}
                                onClick={() => handleProjectModelSave()}
                                disabled={projectModelSaving || !projectModelDirty}
                                sx={{
                                    backgroundColor: '#5757b0',
                                    color: '#fff',
                                    fontWeight: 600,
                                    px: 2.5,
                                    '&:hover': { backgroundColor: '#7777d0' },
                                    minWidth: { xs: '100%', sm: 180 }
                                }}
                            >
                                {projectModelSaving ? (
                                    <CircularProgress size={18} sx={{ color: 'white' }} />
                                ) : (
                                    t('settings.ai.project_model_save', 'Save project settings')
                                )}
                            </Button>
                        </Box>
                    </Box>

                    <Box
                        sx={{
                            width: '100%',
                            maxWidth: '600px',
                            p: { xs: 2.5, sm: 4 },
                            borderRadius: { xs: 2, sm: 3 },
                            background: 'linear-gradient(135deg, #ffffff 0%, #f8f9ff 100%)',
                            border: '1px solid rgba(87, 87, 176, 0.08)',
                            boxShadow: '0 4px 20px rgba(87, 87, 176, 0.08)',
                            transition: 'all 0.3s ease',
                            '&:hover': {
                                boxShadow: '0 8px 30px rgba(87, 87, 176, 0.12)',
                                transform: 'translateY(-2px)',
                                borderColor: 'rgba(87, 87, 176, 0.12)',
                            },
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1.5, sm: 2 }, mb: { xs: 2, sm: 3 } }}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: { xs: 40, sm: 48 },
                                    height: { xs: 40, sm: 48 },
                                    borderRadius: 2,
                                    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                                    boxShadow: '0 4px 15px rgba(16, 185, 129, 0.2)',
                                    flexShrink: 0
                                }}
                            >
                                <TokenIcon sx={{ color: 'white', fontSize: { xs: 20, sm: 24 } }} />
                            </Box>
                            <Box sx={{ flex: 1 }}>
                                <Typography variant={isMobile ? "subtitle1" : "h6"} sx={{ fontWeight: 700, color: '#2d3748', mb: 0.5, lineHeight: 1.2 }}>
                                    {t('settings.ai.token_usage_title', 'AI token usage')}
                                </Typography>
                                <Typography variant="caption" sx={{ color: '#64748b', lineHeight: 1.4, display: 'block' }}>
                                    {t('settings.ai.token_usage_subtitle', 'Project usage for tracked model providers')}
                                </Typography>
                            </Box>
                            <ToggleButtonGroup
                                exclusive
                                size="small"
                                value={tokenUsagePeriod}
                                onChange={handleTokenUsagePeriodChange}
                                sx={{
                                    '& .MuiToggleButton-root': {
                                        px: { xs: 1, sm: 1.5 },
                                        textTransform: 'none',
                                    }
                                }}
                            >
                                <ToggleButton value="day">{t('settings.ai.token_usage_day', 'Day')}</ToggleButton>
                                <ToggleButton value="week">{t('settings.ai.token_usage_week', 'Week')}</ToggleButton>
                                <ToggleButton value="month">{t('settings.ai.token_usage_month', 'Month')}</ToggleButton>
                            </ToggleButtonGroup>
                        </Box>

                        <Stack
                            direction={{ xs: 'column', sm: 'row' }}
                            spacing={1.25}
                            sx={{ mb: 3 }}
                        >
                                {[
                                    ['input_tokens', t('settings.ai.token_usage_input', 'Input')],
                                    ['output_tokens', t('settings.ai.token_usage_output', 'Output')],
                                    ['total_tokens', t('settings.ai.token_usage_total', 'Total')],
                                    ['request_count', t('settings.ai.token_usage_requests', 'Requests')],
                                ].map(([key, label]) => (
                                    <Box
                                        key={key}
                                        sx={{
                                            flex: 1,
                                            minWidth: 0,
                                            border: '1px solid rgba(87, 87, 176, 0.10)',
                                            borderRadius: 2,
                                            px: 1.5,
                                            py: 1.25,
                                            backgroundColor: '#ffffff',
                                            boxShadow: '0 1px 8px rgba(15, 23, 42, 0.03)',
                                        }}
                                    >
                                        <Typography variant="caption" sx={{ color: '#64748b', display: 'block', letterSpacing: '0.02em' }}>
                                            {label}
                                        </Typography>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#111827', lineHeight: 1.35 }}>
                                            {formatTokenNumber(tokenUsageTotals[key])}
                                        </Typography>
                                    </Box>
                                ))}
                            </Stack>

                            <Box
                                sx={{
                                    width: '100%',
                                    height: { xs: 260, sm: 320 },
                                    position: 'relative',
                                    borderRadius: 2,
                                    border: '1px solid rgba(87, 87, 176, 0.10)',
                                    background: 'linear-gradient(180deg, rgba(248, 250, 252, 0.92) 0%, rgba(255, 255, 255, 1) 100%)',
                                    p: { xs: 1, sm: 1.5 },
                                }}
                            >
                                {tokenUsageLoading ? (
                                    <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <CircularProgress size={28} />
                                    </Box>
                                ) : tokenUsageChartData.length > 0 ? (
                                    <ResponsiveContainer width="100%" height="100%">
                                        <ComposedChart data={tokenUsageChartData} margin={{ top: 8, right: 12, left: 0, bottom: 8 }}>
                                            <CartesianGrid strokeDasharray="3 3" stroke="rgba(148, 163, 184, 0.30)" />
                                            <XAxis
                                                dataKey="period"
                                                tick={{ fontSize: 11, fill: '#475569' }}
                                                interval="preserveStartEnd"
                                                minTickGap={12}
                                            />
                                            <YAxis
                                                tick={{ fontSize: 11, fill: '#475569' }}
                                                tickFormatter={(value) => {
                                                    const numericValue = Number(value || 0);
                                                    if (numericValue >= 1000000) return `${(numericValue / 1000000).toFixed(1)}M`;
                                                    if (numericValue >= 1000) return `${Math.round(numericValue / 1000)}K`;
                                                    return String(numericValue);
                                                }}
                                            />
                                            <RechartsTooltip
                                                contentStyle={{
                                                    borderRadius: 12,
                                                    border: '1px solid rgba(87, 87, 176, 0.14)',
                                                    boxShadow: '0 10px 24px rgba(15, 23, 42, 0.10)',
                                                }}
                                                formatter={(value, name) => {
                                                    const labels = {
                                                        input_tokens: t('settings.ai.token_usage_input', 'Input'),
                                                        output_tokens: t('settings.ai.token_usage_output', 'Output'),
                                                        total_tokens: t('settings.ai.token_usage_total', 'Total'),
                                                    };
                                                    return [formatTokenNumber(value), labels[name] || name];
                                                }}
                                            />
                                            <Legend />
                                            <Bar dataKey="input_tokens" name={t('settings.ai.token_usage_input', 'Input')} fill="#0f766e" radius={[4, 4, 0, 0]} />
                                            <Bar dataKey="output_tokens" name={t('settings.ai.token_usage_output', 'Output')} fill="#d97706" radius={[4, 4, 0, 0]} />
                                            <Line type="monotone" dataKey="total_tokens" name={t('settings.ai.token_usage_total', 'Total')} stroke="#2563eb" strokeWidth={2} dot={{ r: 2 }} />
                                        </ComposedChart>
                                    </ResponsiveContainer>
                                ) : (
                                    <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', px: 2 }}>
                                        <Typography variant="body2" sx={{ color: '#64748b', textAlign: 'center', maxWidth: 360 }}>
                                            {t('settings.ai.token_usage_empty', 'No tracked token usage yet for this project.')}
                                        </Typography>
                                    </Box>
                                )}
                            </Box>

                            {topTokenProviders.length > 0 && (
                                <Box sx={{ mt: 2 }}>
                                    <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mb: 1 }}>
                                        {t('settings.ai.token_usage_top_providers', 'Top providers')}
                                    </Typography>
                                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                        {topTokenProviders.map((provider) => (
                                            <Chip
                                                key={provider.provider}
                                                label={`${provider.provider} · ${formatTokenNumber(provider.total_tokens)}`}
                                                size="small"
                                                sx={{
                                                    borderRadius: 999,
                                                    backgroundColor: '#f8fafc',
                                                    border: '1px solid rgba(87, 87, 176, 0.10)',
                                                    color: '#334155',
                                                    fontWeight: 600,
                                                }}
                                            />
                                        ))}
                                    </Stack>
                                </Box>
                            )}
                    </Box>
                </Box>
            </Fade>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={handleSnackbarClose}
                message={snackbar.message}
            />
        </>
    );
};

export default SettingAI;
